import { useEffect, useMemo, useRef } from 'react';
import { cellKey } from '../utils/presenceKeys';

/**
 * useRowPresence(page, rowIds, presence)
 * ------------------------------------------------------------------
 * Announces the rows (orders / quotes / POs) this user currently has open, so
 * everyone else sees "X is also on this row".
 *
 * Row focus is deliberately NOT locked - several people may read the same order
 * at once. Only the editors inside a row (status dropdowns) take a lock.
 *
 * @param {string}   page      presence room id
 * @param {string[]} rowIds    ids of the rows currently open (expanded inline,
 *                             or showing in a details popup)
 * @param {object}   presence  the object returned by usePresence
 */
export default function useRowPresence(page, rowIds, presence) {
    const { reportFocus, reportBlur } = presence;

    // Stable string so the effect only fires when the SET of open rows changes,
    // not on every re-render of a big grid.
    const key = useMemo(
        () => (rowIds || []).filter(Boolean).map(String).sort().join('|'),
        [rowIds]
    );

    const previousRef = useRef([]);

    useEffect(() => {
        const current = key ? key.split('|') : [];
        const previous = previousRef.current;

        previous
            .filter((id) => !current.includes(id))
            .forEach((id) => reportBlur(cellKey(page, 'order', id, 'row')));

        current
            .filter((id) => !previous.includes(id))
            .forEach((id) => reportFocus(cellKey(page, 'order', id, 'row'), { lock: false }));

        previousRef.current = current;
    }, [key, page, reportFocus, reportBlur]);

    // Release everything when the grid unmounts.
    useEffect(
        () => () => {
            previousRef.current.forEach((id) => reportBlur(cellKey(page, 'order', id, 'row')));
            previousRef.current = [];
        },
        [page, reportBlur]
    );
}
